// Pack decoding off the main thread (public/packDecodeWorker.js).
//
// Decoding a v2 pack - meshopt streams, dequantisation, octahedral
// normals - cost ~28 ms of main thread per SpaceX pack (measured
// 2026-09-16, 18 s over a two-minute load), which is frame time taken from
// drawing. A couple of dedicated workers do it instead; the main thread only
// wraps the returned arrays in geometry. Anything that fails - no Worker,
// a worker error - decodes on the main thread as before.
export default class PackDecodePool {
  constructor({ url = '/packDecodeWorker.js', size = null } = {}) {
    const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 2;
    this.url = url;
    this.size = Math.max(1, size ?? Math.min(4, Math.max(1, Math.floor(cores / 2))));
    this.workers = [];
    this.next = 0;
    this.seq = 0;
    this.pending = new Map();
    this.available = typeof Worker !== 'undefined';
    this.stats = { decoded: 0, failed: 0 };
  }

  _worker() {
    if (!this.available) return null;
    if (this.workers.length < this.size) {
      try {
        const w = new Worker(this.url);
        w.onmessage = (e) => this._reply(e.data);
        w.onerror = () => { /* a failed request falls back per call */ };
        this.workers.push(w);
      } catch (e) {
        this.available = false;
        return null;
      }
    }
    const w = this.workers[this.next % this.workers.length];
    this.next += 1;
    return w;
  }

  _reply(msg) {
    const p = msg && this.pending.get(msg.id);
    if (!p) return;
    this.pending.delete(msg.id);
    if (msg.ok) { this.stats.decoded += 1; p.resolve(msg.decoded); } else { this.stats.failed += 1; p.reject(new Error(msg.error || 'pack decode failed')); }
  }

  /** Decoded arrays (see packDecodeCore.decodePackArrays); the buffer is transferred. */
  decode(buffer) {
    const w = this._worker();
    if (!w) return Promise.reject(new Error('no pack decode worker'));
    const id = (this.seq += 1);
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      try {
        w.postMessage({ id, buffer }, [buffer]);
      } catch (e) {
        this.pending.delete(id);
        reject(e);
      }
    });
  }

  dispose() {
    for (const w of this.workers) { try { w.terminate(); } catch (e) { /* gone */ } }
    this.workers = [];
    for (const [, p] of this.pending) p.reject(new Error('pack decode pool disposed'));
    this.pending.clear();
  }
}
